"use client";

import {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { mutedOutlineButtonClass } from "@/components/ui/button";
import { Search, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { SimpleSelect } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AdminTableSerialOrder } from "@/components/admin/admin-table-utils";

export function AdminTableFilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>;
}

export function AdminTableSerialOrderSelect({
  value,
  onChange,
}: {
  value: AdminTableSerialOrder;
  onChange: (value: AdminTableSerialOrder) => void;
}) {
  const t = useTranslations("site.ui");

  return (
    <SimpleSelect
      value={value}
      onValueChange={(nextValue) => {
        if (nextValue === "asc" || nextValue === "desc") {
          onChange(nextValue);
        }
      }}
      className="h-8 w-[88px]"
      contentClassName="min-w-0"
      options={[
        { value: "desc", label: t("descending") },
        { value: "asc", label: t("ascending") },
      ]}
    />
  );
}

export function AdminTableSearchField({
  placeholder,
  value,
  onChange,
  live = true,
  debounceMs = 0,
  className,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;

  live?: boolean;

  debounceMs?: number;

  className?: string;
}) {
  const t = useTranslations("site.ui");
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  function commitSearch(next: string) {
    clearTimeout(timer.current);
    committed.current = next;
    startTransition(() => {
      onChange(next);
    });
  }

  function commitLive(next: string) {
    if (!live) return;

    if (!debounceMs) {
      commitSearch(next);
      return;
    }

    clearTimeout(timer.current);
    timer.current = setTimeout(() => commitSearch(next), debounceMs);
  }

  return (
    <div
      className={cn(
        "relative flex h-8 w-full max-w-sm min-w-0 flex-1 items-center overflow-hidden rounded-lg border border-input bg-transparent transition-colors focus-within:border-[#333] dark:bg-input/30",
        className,
      )}
    >
      <input
        autoComplete="off"
        placeholder={placeholder}
        spellCheck={false}
        className="h-full min-w-0 flex-1 bg-transparent px-2.5 py-1 text-base outline-none placeholder:text-muted-foreground md:text-sm"
        value={draft}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);

          if (!(event.nativeEvent as InputEvent).isComposing) {
            commitLive(next);
          }
        }}
        onCompositionEnd={(event) => commitLive(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.nativeEvent.isComposing) {
            event.preventDefault();
            commitSearch(draft);
          }
        }}
      />
      <button
        type="button"
        className={cn(
          mutedOutlineButtonClass("destructive"),
          "flex shrink-0 items-center px-1 transition-colors",
          draft ? "visible" : "invisible pointer-events-none",
        )}
        aria-label={t("clear")}
        onClick={() => {
          setDraft("");
          commitSearch("");
        }}
      >
        <XIcon className="size-4" />
      </button>
      <button
        type="button"
        aria-label={t("search")}
        className={cn(mutedOutlineButtonClass(), "flex h-full w-8 shrink-0 items-center justify-center border-l border-input transition-colors")}
        onClick={() => commitSearch(draft)}
      >
        <Search className="size-4" />
      </button>
    </div>
  );
}

export function useAdminTableQuery<T>(
  items: T[],
  filterFn: (item: T, query: Record<string, string>) => boolean,
  initialQuery: Record<string, string> = { keyword: "" },
) {
  const [query, setQueryState] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();

  const setQuery = useCallback((patch: Partial<Record<string, string>>) => {
    startTransition(() => {
      setQueryState((current) => {
        const next = { ...current };
        for (const [key, value] of Object.entries(patch)) {
          if (value !== undefined) {
            next[key] = value;
          }
        }
        return next;
      });
    });
  }, []);

  const filtered = useMemo(
    () => items.filter((item) => filterFn(item, query)),
    [items, query, filterFn],
  );
  const rows = useDeferredValue(filtered);
  const paginationResetKey = useMemo(() => JSON.stringify(query), [query]);

  return {
    query,
    setQuery,
    rows,
    isPending: isPending || rows !== filtered,
    paginationResetKey,
  };
}
